import React, { useState, useEffect } from 'react';
import { Modal, Table, Typography, Button, Pagination, Spin, Alert, Tag, Space, Collapse, Input, InputNumber } from 'antd';
import {
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  NumberOutlined,
  EyeOutlined,
  CopyOutlined,
  SyncOutlined,
  SwapOutlined,
  CopyFilled
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';
import {
  useQueueJobs,
  usePromoteJob,
  useDeleteJob,
  useChangeJobPriority,
  useRetryJob,
  useDuplicateJob,
  useMoveJob
} from '../../hooks/useQueueManagement';
import { JobType } from '../../api/queues';
import JSONPretty from 'react-json-pretty';
import 'react-json-pretty/themes/monikai.css';
import { useNavigate } from 'react-router-dom';
import { showSuccessNotification, showErrorNotification } from '../../utils/notificationUtils';

const { Text } = Typography;
const { Panel } = Collapse;

interface QueueJobsModalProps {
  visible: boolean;
  onClose: () => void;
  queueName: string;
  jobType: JobType;
  title?: string;
}

const QueueJobsModal: React.FC<QueueJobsModalProps> = ({
  visible,
  onClose,
  queueName,
  jobType,
  title
}) => {
  const { t: commonT } = useTranslation();
  const { t: queueT } = useTranslationWithNamespace('queue');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [priorityValue, setPriorityValue] = useState<number>(5);
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [moveToQueueName, setMoveToQueueName] = useState<string>('');
  const [isChangePriorityModalVisible, setIsChangePriorityModalVisible] = useState(false);
  const [isMoveToQueueModalVisible, setIsMoveToQueueModalVisible] = useState(false);
  const navigate = useNavigate();

  // Mutations
  const promoteJobMutation = usePromoteJob();
  const deleteJobMutation = useDeleteJob();
  const changeJobPriorityMutation = useChangeJobPriority();
  const retryJobMutation = useRetryJob();
  const duplicateJobMutation = useDuplicateJob();
  const moveJobMutation = useMoveJob();

  // Reset pagination when queue or job type changes
  useEffect(() => {
    setCurrentPage(1);
  }, [queueName, jobType]);

  // Calculate offset for API call
  const offset = (currentPage - 1) * pageSize;

  // Fetch jobs from API
  const { data, isLoading, isError, refetch } = useQueueJobs(
    queueName,
    jobType,
    pageSize,
    offset,
    visible // Only fetch when modal is visible
  );

  // Format job timestamp
  const formatTimestamp = (timestamp: number) => {
    if (!timestamp) return '-';
    return (
      <div style={{ whiteSpace: 'nowrap' }}>
        {new Date(timestamp).toLocaleString()}
      </div>
    );
  };

  // Format job duration
  const formatDuration = (startTime?: number, endTime?: number) => {
    if (!startTime || !endTime) return '-';
    const duration = endTime - startTime;
    if (duration < 1000) return `${duration}ms`;
    return `${(duration / 1000).toFixed(2)}s`;
  };

  // Get job status
  const getJobStatus = (job: any) => {
    if (job.finishedOn && !job.failedReason) return 'completed';
    if (job.failedReason) return 'failed';
    if (job.processedOn) return 'active';
    return 'waiting';
  };

  // Render job status tag
  const renderStatusTag = (status: string) => {
    switch (status) {
      case 'completed':
        return <Tag color="success" icon={<CheckCircleOutlined />}>{queueT('jobStatus.completed')}</Tag>;
      case 'failed':
        return <Tag color="error" icon={<CloseCircleOutlined />}>{queueT('jobStatus.failed')}</Tag>;
      case 'active':
        return <Tag color="processing" icon={<ClockCircleOutlined />}>{queueT('jobStatus.active')}</Tag>;
      case 'waiting':
        return <Tag color="default" icon={<ClockCircleOutlined />}>{queueT('jobStatus.waiting')}</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  // Action handlers
  const handlePromoteJob = (jobId: string) => {
    promoteJobMutation.mutate(
      { queueName, jobId },
      {
        onSuccess: (data) => {
          showSuccessNotification(data.message || `Job ${jobId} promoted to the front of the queue`);
          refetch();
        },
        onError: (error: any) => {
          showErrorNotification(error.message || 'Failed to promote job');
        }
      }
    );
  };

  const handleDeleteJob = (jobId: string) => {
    deleteJobMutation.mutate(
      { queueName, jobId },
      {
        onSuccess: (data) => {
          showSuccessNotification(data.message || `Job ${jobId} deleted from the queue`);
          refetch();
        },
        onError: (error: any) => {
          showErrorNotification(error.message || 'Failed to delete job');
        }
      }
    );
  };

  const handleChangePriority = (jobId: string) => {
    setSelectedJobId(jobId);
    setIsChangePriorityModalVisible(true);
  };

  const handlePriorityChange = () => {
    changeJobPriorityMutation.mutate(
      { queueName, jobId: selectedJobId, priority: priorityValue },
      {
        onSuccess: (data) => {
          showSuccessNotification(data.message || `Priority for job ${selectedJobId} changed to ${priorityValue}`);
          setIsChangePriorityModalVisible(false);
          refetch();
        },
        onError: (error: any) => {
          showErrorNotification(error.message || 'Failed to change job priority');
        }
      }
    );
  };

  const handleViewTransaction = (job: any) => {
    // Extract transaction ID from job data and navigate to transaction details
    const transactionId = job.data?.transactionId || job.data?.transaction?._id || job.data?._id;
    if (transactionId) {
      navigate(`/transactions/details/${transactionId}`);
    } else {
      // If no direct transaction ID, try to find it in the job data
      console.log('Job data:', job.data);
      if (job.data && typeof job.data === 'object') {
        // Look for any field that might contain a transaction ID
        const possibleIdFields = ['_id', 'id', 'transactionId', 'transaction_id', 'txid'];
        for (const field of possibleIdFields) {
          if (job.data[field]) {
            navigate(`/transactions/details/${job.data[field]}`);
            return;
          }
        }
      }
      showErrorNotification('Transaction ID not found in job data');
    }
  };

  const handleDuplicateJob = (job: any) => {
    duplicateJobMutation.mutate(
      { queueName, jobId: job.id },
      {
        onSuccess: (data) => {
          showSuccessNotification(data.message || `Job ${job.id} duplicated`);
          refetch();
        },
        onError: (error: any) => {
          showErrorNotification(error.message || 'Failed to duplicate job');
        }
      }
    );
  };

  const handleRetryJob = (jobId: string) => {
    retryJobMutation.mutate(
      { queueName, jobId },
      {
        onSuccess: (data) => {
          showSuccessNotification(data.message || `Job ${jobId} retried`);
          refetch();
        },
        onError: (error: any) => {
          showErrorNotification(error.message || 'Failed to retry job');
        }
      }
    );
  };

  const handleMoveToQueue = (jobId: string) => {
    setSelectedJobId(jobId);
    setIsMoveToQueueModalVisible(true);
  };

  const handleMoveToQueueSubmit = () => {
    if (!moveToQueueName) {
      showErrorNotification('Please enter a target queue name');
      return;
    }

    moveJobMutation.mutate(
      { sourceQueue: queueName, targetQueue: moveToQueueName, jobId: selectedJobId },
      {
        onSuccess: (data) => {
          showSuccessNotification(data.message || `Job ${selectedJobId} moved to queue ${moveToQueueName}`);
          setIsMoveToQueueModalVisible(false);
          setMoveToQueueName('');
          refetch();
        },
        onError: (error: any) => {
          showErrorNotification(error.message || 'Failed to move job');
        }
      }
    );
  };

  const handleCopyJobId = (jobId: string) => {
    navigator.clipboard.writeText(jobId);
    showSuccessNotification('Job ID copied to clipboard');
  };

  // Render action buttons based on job status
  const renderActionButtons = (record: any) => {
    const status = getJobStatus(record);
    const jobId = record.id;

    // Common action - Copy ID
    const copyButton = (
      <Button
        key="copy"
        icon={<CopyOutlined />}
        size="small"
        onClick={() => handleCopyJobId(jobId)}
        title={queueT('jobActions.copyId')}
      />
    );

    switch (status) {
      case 'waiting':
        return (
          <Space>
            <Button
              key="promote"
              type="primary"
              icon={<ArrowUpOutlined />}
              size="small"
              onClick={() => handlePromoteJob(jobId)}
              title={queueT('jobActions.promote')}
            />
            <Button
              key="priority"
              icon={<NumberOutlined />}
              size="small"
              onClick={() => handleChangePriority(jobId)}
              title={queueT('jobActions.changePriority')}
            />
            <Button
              key="delete"
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDeleteJob(jobId)}
              title={queueT('jobActions.delete')}
            />
            {copyButton}
          </Space>
        );

      case 'active':
        return (
          <Space>
            <Button
              key="view"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewTransaction(record)}
              title={queueT('jobActions.viewTransaction')}
            />
            {copyButton}
          </Space>
        );

      case 'completed':
        return (
          <Space>
            <Button
              key="view"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewTransaction(record)}
              title={queueT('jobActions.viewTransaction')}
            />
            <Button
              key="duplicate"
              icon={<CopyFilled />}
              size="small"
              onClick={() => handleDuplicateJob(record)}
              title={queueT('jobActions.duplicate')}
            />
            {copyButton}
          </Space>
        );

      case 'failed':
        return (
          <Space>
            <Button
              key="retry"
              type="primary"
              icon={<SyncOutlined />}
              size="small"
              onClick={() => handleRetryJob(jobId)}
              title={queueT('jobActions.retry')}
            />
            <Button
              key="move"
              icon={<SwapOutlined />}
              size="small"
              onClick={() => handleMoveToQueue(jobId)}
              title={queueT('jobActions.moveToQueue')}
            />
            <Button
              key="view"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewTransaction(record)}
              title={queueT('jobActions.viewTransaction')}
            />
            <Button
              key="delete"
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDeleteJob(jobId)}
              title={queueT('jobActions.delete')}
            />
            {copyButton}
          </Space>
        );

      default:
        return copyButton;
    }
  };

  // Table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px' }}>
          <Text copyable>{id}</Text>
        </div>
      ),
      width: 150
    },
    {
      title: queueT('jobColumns.status'),
      key: 'status',
      render: (_: any, record: any) => renderStatusTag(getJobStatus(record)),
      width: 120
    },
    {
      title: queueT('jobColumns.timestamp'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp: number) => formatTimestamp(timestamp),
      width: 180
    },
    {
      title: queueT('jobColumns.processedOn'),
      dataIndex: 'processedOn',
      key: 'processedOn',
      render: (processedOn: number) => formatTimestamp(processedOn),
      width: 180
    },
    {
      title: queueT('jobColumns.finishedOn'),
      dataIndex: 'finishedOn',
      key: 'finishedOn',
      render: (finishedOn: number) => formatTimestamp(finishedOn),
      width: 180
    },
    {
      title: queueT('jobColumns.duration'),
      key: 'duration',
      render: (_: any, record: any) => formatDuration(record.processedOn, record.finishedOn),
      width: 100
    },
    {
      title: queueT('jobColumns.attempts'),
      dataIndex: 'attemptsMade',
      key: 'attemptsMade',
      width: 100
    },
    {
      title: queueT('jobColumns.actions'),
      key: 'actions',
      render: (_: any, record: any) => renderActionButtons(record),
      width: 200
    }
  ];

  // Expandable row render function
  const expandedRowRender = (record: any) => (
    <Collapse>
      <Panel header={queueT('jobDetails.data')} key="data">
        <JSONPretty id={`json-data-${record.id}`} data={record.data} theme="monikai" />
      </Panel>
      {record.opts && (
        <Panel header={queueT('jobDetails.options')} key="options">
          <JSONPretty id={`json-opts-${record.id}`} data={record.opts} theme="monikai" />
        </Panel>
      )}
      {record.failedReason && (
        <Panel header={queueT('jobDetails.error')} key="error">
          <Alert message={record.failedReason} type="error" />
        </Panel>
      )}
    </Collapse>
  );

  // Modal title based on job type
  const getModalTitle = () => {
    if (title) return title;

    let jobTypeText = '';
    switch (jobType) {
      case JobType.WAITING:
        jobTypeText = queueT('waiting');
        break;
      case JobType.ACTIVE:
        jobTypeText = queueT('active');
        break;
      case JobType.COMPLETED:
        jobTypeText = queueT('completed');
        break;
      case JobType.FAILED:
        jobTypeText = queueT('failed');
        break;
      case JobType.DELAYED:
        jobTypeText = queueT('delayed');
        break;
    }

    return `${jobTypeText} ${queueT('jobs')} - ${queueName}`;
  };

  return (
    <>
      <Modal
        title={getModalTitle()}
        open={visible}
        onCancel={onClose}
        width={1200}
        footer={[
          <Button key="refresh" icon={<ReloadOutlined />} onClick={() => refetch()}>
            {commonT('actions.refresh')}
          </Button>,
          <Button key="close" onClick={onClose}>
            {commonT('actions.close')}
          </Button>
        ]}
      >
        {isError ? (
          <Alert
            message={queueT('error.fetchJobs')}
            description={queueT('error.fetchJobsDescription')}
            type="error"
            showIcon
          />
        ) : (
          <>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <>
                <Table
                  columns={columns}
                  dataSource={data?.jobs || []}
                  rowKey="id"
                  pagination={false}
                  expandable={{
                    expandedRowRender,
                    expandRowByClick: true
                  }}
                  locale={{ emptyText: queueT('noJobs') }}
                  scroll={{ x: 1110 }}
                  bordered
                  size="middle"
                />

                {data?.pagination && data.pagination.total > 0 && (
                  <div style={{ textAlign: 'right', marginTop: 16 }}>
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={data.pagination.total}
                      onChange={(page, pageSize) => {
                        setCurrentPage(page);
                        setPageSize(pageSize);
                      }}
                      showSizeChanger
                      showQuickJumper
                      showTotal={(total) => queueT('pagination.total', { total })}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Modal>

      {/* Change Priority Modal */}
      <Modal
        title={queueT('jobActions.changePriority')}
        open={isChangePriorityModalVisible}
        onCancel={() => setIsChangePriorityModalVisible(false)}
        onOk={handlePriorityChange}
        okText={commonT('actions.save')}
        cancelText={commonT('actions.cancel')}
      >
        <div style={{ marginBottom: 16 }}>
          <p>{queueT('jobActions.changePriorityDescription')}</p>
          <InputNumber
            min={1}
            max={10}
            defaultValue={5}
            value={priorityValue}
            onChange={(value) => setPriorityValue(value || 5)}
            style={{ width: '100%' }}
          />
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">{queueT('jobActions.priorityHint')}</Text>
          </div>
        </div>
      </Modal>

      {/* Move to Queue Modal */}
      <Modal
        title={queueT('jobActions.moveToQueue')}
        open={isMoveToQueueModalVisible}
        onCancel={() => setIsMoveToQueueModalVisible(false)}
        onOk={handleMoveToQueueSubmit}
        okText={commonT('actions.move')}
        cancelText={commonT('actions.cancel')}
      >
        <div style={{ marginBottom: 16 }}>
          <p>{queueT('jobActions.moveToQueueDescription')}</p>
          <Input
            placeholder={queueT('jobActions.enterQueueName')}
            value={moveToQueueName}
            onChange={(e) => setMoveToQueueName(e.target.value)}
            style={{ width: '100%' }}
          />
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">{queueT('jobActions.moveToQueueHint')}</Text>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default QueueJobsModal;
